import {
  InvestorDetailsPacingTabAutoPlanWizardModalPageAutoPlanMutation,
  InvestorDetailsPacingTabCommitmentPlanQuery
} from '@/__generated__/graphql';
import ModalWrappedContent from '@/components/basicComponents/modal/modalWrappedContent';
import Input from '@/components/basicComponents/input';
import { formatPercentageInputValue, handleCursorPositionInPercentageInput } from '@/components/basicComponents/input/utils';
import { themeVar } from '@/styles/theme';
import { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import styled from 'styled-components';

interface ModelOptionsProps {
  isOpen: boolean;
  commitmentPlanData:
    | InvestorDetailsPacingTabCommitmentPlanQuery['commitmentPlan']
    | InvestorDetailsPacingTabAutoPlanWizardModalPageAutoPlanMutation['autoPlan']
    | null;
  saveModelOptions: (name: string, growthRate: number) => void;
  onClose: () => void;
}

const ModelOptions = ({ isOpen, commitmentPlanData, saveModelOptions, onClose }: ModelOptionsProps) => {
  const {
    handleSubmit,
    getValues,
    control,
    reset,
    formState: { isValid }
  } = useForm();

  useEffect(() => {
    if (!commitmentPlanData) return;
    reset({ name: commitmentPlanData.name, growthRate: commitmentPlanData.growthRate + '%' });
  }, [commitmentPlanData]);

  const saveValue = () => {
    if (!isValid) return;
    const { name, growthRate } = getValues();

    saveModelOptions(name.trim(), parseFloat(growthRate));
  };

  const validatePercentage = (value: string) => {
    const numericValue = parseFloat(value.replace('%', ''));
    if (isNaN(numericValue)) {
      return 'Invalid number';
    }
    if (numericValue < 0) {
      return 'Value must be at least 0';
    }
    if (numericValue > 100) {
      return 'Value must be at most 100';
    }
    return true;
  };

  return (
    <ModalWrappedContent
      isOpen={isOpen}
      onClose={onClose}
      title="Model Options"
      confirmButton={{
        name: 'Confirm',
        onClick: handleSubmit(saveValue)
      }}
      showRequiredFields
    >
      <ModalBody>
        <SubTitle>Changes here will update the model forecast but will not alter the existing commitment plan.</SubTitle>
        <Controller
          name="name"
          control={control}
          rules={{
            required: {
              value: true,
              message: 'Plan Name is required'
            }
          }}
          render={({ field: { onChange, value }, fieldState: { error } }) => (
            <Input value={value || ''} onChange={onChange} errorText={error?.message} label="Plan Name" size="lg" isRequiredField />
          )}
        />
        <Controller
          name="growthRate"
          control={control}
          rules={{
            required: {
              value: true,
              message: 'Growth Rate ex. Drawdowns is required'
            },
            validate: validatePercentage
          }}
          render={({ field: { onChange, value }, fieldState: { error } }) => (
            <Input
              value={value || ''}
              onChange={(e) => {
                const formattedValue = formatPercentageInputValue(e);
                onChange(formattedValue);
              }}
              onClick={handleCursorPositionInPercentageInput}
              onKeyUp={handleCursorPositionInPercentageInput}
              errorText={error?.message}
              label="Growth Rate ex. Drawdowns"
              size="lg"
              isRequiredField
            />
          )}
        />
      </ModalBody>
    </ModalWrappedContent>
  );
};

export default ModelOptions;

const ModalBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 30px;
`;

const SubTitle = styled.p`
  text-align: center;
  color: ${themeVar('font.base')};
  font-size: 19px;
  font-weight: 400;
  line-height: 26.6px;
`;
